import moment from 'moment';
import momentTimezone from 'moment-timezone';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import { CARD_TYPE } from '../constants/cardType';
import { TENANT_TIMEZONE } from '../constants/business';
import { TIME_FORMAT } from '../constants/time';

// State prefix, then the file number; same rule as patient-api's cover validation.
export const DVA_NUMBER_PATTERN =
  /^[NVQSWT]([A-Z ][0-9]{1,6}|[A-Z]{2}[0-9]{1,5}|[A-Z]{3}[0-9]{1,4})[A-Z]?$/;

const MEDICARE_CHECK_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9];

/**
 * A Medicare card number: first digit 2-6, and the ninth digit the weighted sum of the first
 * eight mod 10. Same rule as patient-api's cover validation.
 */
export const isValidMedicareNumber = (value) =>
  /^[2-6]\d{9}$/.test(String(value || '')) &&
  MEDICARE_CHECK_WEIGHTS.reduce(
    (sum, weight, index) => sum + weight * Number(value[index]),
    0
  ) %
    10 ===
    Number(value[8]);

export const MEDICARE_IRN_PATTERN = /^[1-9]$/;

/** Whether the form holds exactly the Medicare card on file (so nothing would be sent). */
export const isUnchangedMedicareOnFile = (
  { medicareCardNumber, medicareCardIRN, expiry } = {},
  coverOnFile
) =>
  coverOnFile?.cardType === CARD_TYPE.MEDICARE &&
  String(medicareCardNumber || '').trim() === coverOnFile.medicareCardNumber &&
  String(medicareCardIRN || '').trim() === coverOnFile.medicareCardIRN &&
  Boolean(expiry) &&
  Boolean(coverOnFile.expiredDate) &&
  moment(expiry).format('YYYY-MM') ===
    moment(coverOnFile.expiredDate).format('YYYY-MM');

/** A card is valid to the end of its expiry month (tenant zone), as patient-api checks. */
export const isExpiryCurrent = (expiry, now = moment()) =>
  Boolean(expiry) &&
  moment(expiry).isValid() &&
  moment(expiry).format('YYYY-MM') >=
    momentTimezone(now).tz(TENANT_TIMEZONE).format('YYYY-MM');

export const PAYMENT_STATE = {
  PAID: 'paid',
  UNPAID: 'unpaid',
  // Paid outside the link; our team arranges it.
  MANUAL: 'manual',
  NONE: 'none',
};

/**
 * Mirrors patient-api's choice of reschedule SMS: paid; unpaid (a fee is owed online and
 * nothing has been paid); manual (staff arrange the payment); or none (no fee, a zero fee,
 * bulk billed, or a refund in progress).
 */
export const getPaymentState = (payment) => {
  const status = String(payment?.currentStatus?.status || '')
    .trim()
    .toLowerCase();
  if (status === PAYMENT_STATE.PAID) return PAYMENT_STATE.PAID;
  if (status) return PAYMENT_STATE.NONE;
  const paymentInfo = payment?.paymentInfo;
  if (!isPlainObject(paymentInfo) || isEmpty(paymentInfo)) {
    return PAYMENT_STATE.NONE;
  }
  if (paymentInfo.type === 'manual') return PAYMENT_STATE.MANUAL;
  return Number(paymentInfo.fee) === 0
    ? PAYMENT_STATE.NONE
    : PAYMENT_STATE.UNPAID;
};

const parseStored = (dateTime) =>
  momentTimezone.parseZone(String(dateTime || ''), TIME_FORMAT.ISO);

/** Length of the current appointment; the new time keeps it. */
export const getAppointmentDurationMinutes = (appointment) => {
  const start = parseStored(appointment?.startDateTime);
  const end = parseStored(appointment?.endDateTime);
  if (!start.isValid() || !end.isValid()) return null;
  const minutes = end.diff(start, 'minutes');
  return minutes > 0 ? minutes : null;
};

const addWeekdays = (start, days) => {
  const result = start.clone();
  let added = 0;
  while (added < days) {
    result.add(1, 'day');
    if (result.isoWeekday() <= 5) added += 1;
  }
  return result;
};

/**
 * Whether a chosen time falls inside the 2-business-day cancellation window, so the patient
 * can be warned before confirming. Weekdays only: public holidays are not known here, which
 * at worst leaves out a warning the server-side rule would still enforce.
 */
export const isInsideCancellationWindow = (
  startIso,
  now = moment(),
  businessDays = 2
) => {
  const start = momentTimezone(startIso, moment.ISO_8601, true);
  if (!start.isValid()) return false;
  const threshold = addWeekdays(
    momentTimezone(now).tz(TENANT_TIMEZONE),
    businessDays
  );
  return !start.isAfter(threshold);
};

// Stored expiries are YYYY-MM-DD or MM/YYYY; compare and prefill as YYYY-MM-DD.
const STORED_EXPIRY_FORMATS = [
  TIME_FORMAT.DATE_WITH_DASH,
  TIME_FORMAT.TABLE_MONTH,
];
const normaliseStoredExpiry = (value) => {
  if (!value) return undefined;
  const parsed = moment(value, STORED_EXPIRY_FORMATS, true);
  return parsed.isValid()
    ? parsed.format(TIME_FORMAT.DATE_WITH_DASH)
    : undefined;
};

/** What the record holds: a Medicare card, a DVA card, or neither. */
export const getCoverOnFile = (medicare) => {
  const record = medicare || {};
  if (
    record.cardType === CARD_TYPE.DVA ||
    (!record.cardType && record.dvaNumber)
  ) {
    return record.dvaNumber
      ? {
          cardType: CARD_TYPE.DVA,
          dvaNumber: record.dvaNumber,
          dvaCardType: record.dvaCardType,
        }
      : { cardType: CARD_TYPE.NONE };
  }
  if (record.medicareCardNumber) {
    return {
      cardType: CARD_TYPE.MEDICARE,
      medicareCardNumber: record.medicareCardNumber,
      medicareCardIRN: record.medicareCardIRN,
      expiredDate: normaliseStoredExpiry(record.medicareCardExpireDate),
    };
  }
  return { cardType: CARD_TYPE.NONE };
};

/**
 * Which cards the details step offers (SE-730): the card on the record and only that, so a
 * patient cannot drop a card on file from here. With none on file they may add a Medicare
 * or a DVA card, or carry on without one.
 */
export const getCoverChoices = (coverOnFile) => {
  const cardType = coverOnFile?.cardType;
  if (cardType === CARD_TYPE.MEDICARE || cardType === CARD_TYPE.DVA) {
    return [cardType];
  }
  return [CARD_TYPE.MEDICARE, CARD_TYPE.DVA, CARD_TYPE.NONE];
};

/**
 * The `cover` to send with the reschedule, or undefined when the patient kept what was on
 * file or chose no card. patient-api checks again and only writes real changes.
 */
export const buildCoverPayload = (formCover, coverOnFile) => {
  const cardType = formCover?.cardType;

  if (cardType === CARD_TYPE.MEDICARE) {
    const payload = {
      cardType,
      medicareCardNumber: String(formCover.medicareCardNumber || '').trim(),
      medicareCardIRN: String(formCover.medicareCardIRN || '').trim(),
      expiredDate: formCover.expiry
        ? moment(formCover.expiry).format(TIME_FORMAT.DATE_WITH_DASH)
        : '',
    };
    const unchanged =
      coverOnFile?.cardType === CARD_TYPE.MEDICARE &&
      coverOnFile.medicareCardNumber === payload.medicareCardNumber &&
      coverOnFile.medicareCardIRN === payload.medicareCardIRN &&
      coverOnFile.expiredDate === payload.expiredDate;
    return unchanged ? undefined : payload;
  }

  if (cardType === CARD_TYPE.DVA) {
    const payload = {
      cardType,
      dvaNumber: String(formCover.dvaNumber || '')
        .trim()
        .toUpperCase(),
      dvaCardType: formCover.dvaCardType,
    };
    const unchanged =
      coverOnFile?.cardType === CARD_TYPE.DVA &&
      coverOnFile.dvaNumber === payload.dvaNumber &&
      coverOnFile.dvaCardType === payload.dvaCardType;
    return unchanged ? undefined : payload;
  }

  return undefined;
};

/**
 * Slot options without the appointment's current start: picking it would be no change.
 * Compared as instants, so the zone the patient views in doesn't matter.
 */
export const withoutCurrentStart = (
  timeslots,
  currentStartDateTime,
  timezone
) => {
  const current = parseStored(currentStartDateTime);
  if (!current.isValid() || !timezone) return timeslots;
  return timeslots.filter(
    ({ value }) =>
      !momentTimezone
        .tz(value, TIME_FORMAT.DATE_AND_TIME, timezone)
        .isSame(current)
  );
};
